import { z } from "zod";
import { addMonths, addWeeks, format } from "date-fns";
import {
  AreaSeries,
  Axis,
  Box,
  ChartGrid,
  ChartTooltip,
  LegendOrdinal,
  LineSeries,
  WidgetEmpty,
  WidgetSkeleton,
  XYChart,
  compactNumber,
  useCategoricalColors,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { UNASSIGNED_DIMENSION_KEY } from "#app/widgets/dashboards/use-dimension-counts";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { seriesLineTitle } from "../shared/derived-titles";
import { useSeriesLine, type LinePoint } from "./use-series-line";

/**
 * `series-line` — a themed time-series line chart of projects over time on the
 * `XYChart` scaffold: one line for the total, or one line per stage ("stage over
 * time"). Reads the `useSeriesLine` reader, which asks the SHARED server count
 * aggregation (`useIssueGroupCounts`) for exact per-period counts (week/month
 * buckets, optional running total) — no client sample. Each split line wears
 * its entity's OWN color (`useCategoricalColors` — stage/type/workflow icon
 * color, ramp fallback); a single total line uses the first ramp hue. Serves
 * the Custom "Line chart" and the Stages "Stage over time" presets.
 */
export const seriesLineConfigSchema = z
  .looseObject({
    measure: z.enum(["created", "updated"]).default("created"),
    groupBy: z.enum(["none", "stage", "type", "workflow"]).default("none"),
    cumulative: z.boolean().default(false),
    area: z.boolean().default(false),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
    filters: widgetFiltersSchema.nullish(),
  });

export type SeriesLineConfig = z.infer<typeof seriesLineConfigSchema>;

// The band x value is the numeric bucket start (ms), NOT the label: distinct
// timestamps keep buckets from colliding on the band even when labels repeat,
// and they land in true chronological order.
const xAccessor = (d: LinePoint) => d.t;
const yAccessor = (d: LinePoint) => d.y;

function SeriesLineRender({ w, h, config }: WidgetRenderContext<SeriesLineConfig>) {
  const { series, bucket, loading, error } = useSeriesLine(
    config.measure,
    config.groupBy,
    config.cumulative,
    config.filters,
  );
  const sliceColors = useCategoricalColors(series);
  // Click-through: a line point opens this card's Data drawer filtered to that
  // bucket (and, on a stage-split line, that stage).
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="rows" />;
  }
  if (series.length === 0) {
    return <WidgetEmpty icon="trending-up" title="No projects in range" />;
  }

  const compact = h < 3;
  const showAxes = config.showAxes && !compact;
  const showGrid = config.showGrid && !compact;
  const multi = series.length > 1;
  const useArea = config.area && !multi;
  const showLegend = multi && h >= 3;

  // Sample the band axis so many buckets don't crowd the labels. Ticks are the
  // numeric bucket starts; the format compacts by span — a single year drops the
  // year, a multi-year span shows a short "Jan '24" so buckets stay legible.
  const points = series[0]?.points ?? [];
  const maxTicks = Math.max(3, Math.round(w * 1.2));
  const step = Math.max(1, Math.ceil(points.length / maxTicks));
  const tickValues = points.filter((_, i) => i % step === 0).map((p) => p.t);
  const first = points[0];
  const last = points.at(-1);
  const multiYear =
    first !== undefined &&
    last !== undefined &&
    new Date(first.t).getFullYear() !== new Date(last.t).getFullYear();
  const axisFmt = multiYear ? "MMM ''yy" : bucket === "month" ? "MMM" : "MMM d";
  const formatTick = (v: number) => format(new Date(v), axisFmt);
  const labelById = new Map(series.map((s) => [s.id, s.label]));

  return (
    <Box direction="column" fill="flex" gap="xs" cursor={drill && instance ? "pointer" : undefined}>
      <XYChart<LinePoint>
        xScale={{ type: "band", paddingInner: 0.2, paddingOuter: 0.1 }}
        yScale={{ type: "linear", nice: true }}
        margin={{ top: 8, right: 8, bottom: showAxes ? 36 : 8, left: showAxes ? 36 : 8 }}
        ariaLabel="Projects over time"
        onPointerUp={
          drill && instance
            ? ({ datum, key }) => {
                if (!datum) return;
                const bucketStart = new Date(datum.t);
                const endMs =
                  bucket === "month"
                    ? addMonths(bucketStart, 1).getTime()
                    : addWeeks(bucketStart, 1).getTime();
                // A split point narrows to its category; the no-value bucket
                // (__unassigned__) and the single total line (__all__) carry no
                // dimension. A cumulative point is a running total, so it reaches
                // back to the start of time (−∞) rather than just this bucket.
                const hasSplitKey =
                  key !== UNASSIGNED_DIMENSION_KEY && key !== "__all__";
                const splitDimension =
                  hasSplitKey && config.groupBy === "stage"
                    ? "stage"
                    : hasSplitKey && config.groupBy === "type"
                      ? "type"
                      : hasSplitKey && config.groupBy === "workflow"
                        ? "workflow"
                        : undefined;
                const seriesLabel = key ? labelById.get(key) : undefined;
                // The chip names the whole bucket period, never a single day: a
                // month bucket reads "January 2026", a week "Week of Jan 5, 2026"
                // — so a week's "MMM d" label can't be mistaken for one day.
                const periodLabel =
                  bucket === "month"
                    ? format(bucketStart, "MMMM yyyy")
                    : `Week of ${format(bucketStart, "MMM d, yyyy")}`;
                drill.openData(instance.instanceId, {
                  kind: "dateRange",
                  dateField: config.measure === "updated" ? "lastUpdateDate" : "creationDate",
                  startMs: config.cumulative ? Number.NEGATIVE_INFINITY : datum.t,
                  endMs,
                  dimension: splitDimension,
                  ids: splitDimension ? [key] : undefined,
                  label: multi && seriesLabel ? `${seriesLabel}: ${periodLabel}` : periodLabel,
                });
              }
            : undefined
        }
      >
        {showGrid ? <ChartGrid rows columns={false} numTicks={4} /> : null}
        {showAxes ? <Axis orientation="left" numTicks={4} tickFormat={compactNumber} /> : null}
        {showAxes ? (
          <Axis orientation="bottom" tickValues={tickValues} tickFormat={(v) => formatTick(Number(v))} />
        ) : null}
        {series.map((s) =>
          useArea ? (
            <AreaSeries
              key={s.id}
              dataKey={s.id}
              data={s.points}
              xAccessor={xAccessor}
              yAccessor={yAccessor}
              renderLine
              colorAccessor={() => sliceColors.scale(s.label)}
            />
          ) : (
            <LineSeries
              key={s.id}
              dataKey={s.id}
              data={s.points}
              xAccessor={xAccessor}
              yAccessor={yAccessor}
              colorAccessor={() => sliceColors.scale(s.label)}
            />
          ),
        )}
        <ChartTooltip
          snapTooltipToDatumX
          showVerticalCrosshair
          renderTooltip={({ tooltipData }) => {
            const near = tooltipData?.nearestDatum;
            const d = near?.datum as LinePoint | undefined;
            if (!d) return null;
            const seriesLabel = near?.key ? labelById.get(near.key) : undefined;
            return (
              <WidgetChartTooltip
                label={d.label}
                details={`${multi && seriesLabel ? `${seriesLabel}: ` : ""}${d.y} ${d.y === 1 ? "project" : "projects"}`}
              />
            );
          }}
        />
      </XYChart>
      {showLegend ? (
        <LegendOrdinal scale={sliceColors.scale} direction="row" itemMargin="0 12px 0 0" shapeMargin="0 6px 0 0" />
      ) : null}
    </Box>
  );
}

const seriesLineSettings: WidgetSettingsGroup[] = [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  {
    title: "Data",
    fields: [
      {
        key: "measure",
        kind: "select",
        label: "Measure",
        icon: "trending-up",
        options: [
          { value: "created", label: "Projects created" },
          { value: "updated", label: "Projects updated" },
        ],
      },
      {
        key: "groupBy",
        kind: "select",
        label: "Group by",
        icon: "layers",
        options: [
          { value: "none", label: "Total" },
          { value: "stage", label: "Stage" },
          { value: "type", label: "Type" },
          { value: "workflow", label: "Workflow" },
        ],
      },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "cumulative", kind: "toggle", label: "Running total", icon: "chart-column-increasing" },
      { key: "area", kind: "toggle", label: "Fill area", icon: "activity" },
      { key: "showGrid", kind: "toggle", label: "Gridlines", icon: "grid-dots" },
      { key: "showAxes", kind: "toggle", label: "Axes", icon: "ruler" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const seriesLineDef: WidgetModule<SeriesLineConfig> = {
  type: "series-line",
  title: "Line chart",
  dynamicTitle: (config) => seriesLineTitle(config.title, config.groupBy),
  icon: "trending-up",
  span: { minW: 3, minH: 2, maxW: 12, maxH: 8, defaultW: 6, defaultH: 4 },
  configSchema: seriesLineConfigSchema,
  settings: seriesLineSettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "series-line.custom",
      label: "Line chart",
      description: "Track projects created or updated over time, as a line.",
      section: "Custom",
      defaultConfig: { groupBy: "none" },
      defaultW: 6,
      defaultH: 4,
    },
    {
      key: "series-line.stage",
      label: "Stage over time",
      description: "How projects move through each stage over time, one line per stage.",
      section: "Charts",
      tags: ["projects", "stages"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "stage" },
      defaultW: 6,
      defaultH: 4,
      // The label matches the derived title, so stay untitled and let
      // `dynamicTitle` track `groupBy` as the user regroups.
      autoTitle: false,
    },
  ],
  render: SeriesLineRender,
};
